import {
  Box,
  Button,
  createRecipeContext,
  type HTMLChakraProps,
  HStack,
  Icon,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react'
import {
  LuCalendarPlus,
  LuChevronsLeft,
  LuExternalLink,
  LuFileClock,
  LuLogOut,
  LuMenu,
  LuReceiptEuro,
  LuSquarePlus,
  LuUser,
} from 'react-icons/lu'

import { sibHeaderRecipe } from '@/theme/recipes/sib-header.recipe'

type SibHeaderProps = {
  userName?: string
  sidebarCollapsed?: boolean
  onToggleSidebar?: () => void
  onOpenMobileSidebar?: () => void
  onNewOrder: () => void
  onAddActivity: () => void
  onCreateInvoice: () => void
  onGoDashboard: () => void
  onOpenSupport: () => void
  onLogout: () => void
}

export const SIB_HEADER_HEIGHT = 72

const { withContext } = createRecipeContext({ recipe: sibHeaderRecipe })

const HeaderRoot = withContext<HTMLElement, HTMLChakraProps<'header'>>('header')

export function SibHeader({
  userName,
  sidebarCollapsed = false,
  onToggleSidebar,
  onOpenMobileSidebar,
  onNewOrder,
  onAddActivity,
  onCreateInvoice,
  onGoDashboard,
  onOpenSupport,
  onLogout,
}: SibHeaderProps) {
  const hasSidebarControls = Boolean(onToggleSidebar || onOpenMobileSidebar)

  return (
    <HeaderRoot height={`${SIB_HEADER_HEIGHT}px`}>
      <HStack gap={2} minW={0}>
        {hasSidebarControls && (
          <>
            <Button
              display={{ base: 'none', md: 'inline-flex' }}
              colorPalette="white"
              variant="outline"
              onClick={onToggleSidebar}
              aria-label="Sidebar umschalten"
            >
              <Box
                transform={sidebarCollapsed ? 'scaleX(-1)' : 'scaleX(1)'}
                transition="transform 0.3s ease"
              >
                <LuChevronsLeft />
              </Box>
            </Button>

            <Button
              display={{ base: 'inline-flex', md: 'none' }}
              variant="plain"
              onClick={onOpenMobileSidebar}
              aria-label="Navigation öffnen"
            >
              <LuMenu />
            </Button>
          </>
        )}

        {/*
          The trigger id is handed to Menu.Root via `ids` rather than set on the
          Button. Setting it directly overwrites the id Zag generates for the
          trigger, which breaks the reference Floating UI measures against: the
          menu then reports a 0x0 reference and anchors to the viewport origin
          instead of the button. The OnboardingWizard targets #new-event-button.
        */}
        <Menu.Root ids={{ trigger: 'new-event-button' }}>
          <Menu.Trigger asChild>
            <Button colorPalette="black" variant="solid">
              <Text>Ereignis hinzufügen</Text>
              <LuSquarePlus />
            </Button>
          </Menu.Trigger>

          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item value="new-order" onClick={onNewOrder}>
                  <LuFileClock />
                  Neue Bestellung
                </Menu.Item>
                <Menu.Item value="add-activity" onClick={onAddActivity}>
                  <LuCalendarPlus />
                  Aktivität hinzufügen
                </Menu.Item>
                <Menu.Item value="create-invoice" onClick={onCreateInvoice}>
                  <LuReceiptEuro />
                  Rechnung erstellen
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>

        <HStack gap={2} display={{ base: 'none', md: 'flex' }}>
          <Button variant="plain" onClick={onGoDashboard}>
            Dashboard
          </Button>
          <Button variant="plain" onClick={onOpenSupport}>
            Support &amp; Feedback
            <LuExternalLink />
          </Button>
        </HStack>
      </HStack>

      <Menu.Root positioning={{ placement: 'bottom-end' }}>
        <Menu.Trigger asChild>
          <Button variant="ghost">
            <LuUser />
            <Text display={{ base: 'none', sm: 'block' }}>{userName || 'Konto'}</Text>
          </Button>
        </Menu.Trigger>

        <Portal>
          <Menu.Positioner>
            <Menu.Content>
              <Menu.Item value="logout" onClick={onLogout}>
                <HStack>
                  <Icon as={LuLogOut} />
                  <Text>Abmelden</Text>
                </HStack>
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </HeaderRoot>
  )
}
